"use client";

import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { useToast } from "@/components/ui/toast";
import { GENERIC_ERROR_MESSAGE } from "@/lib/errors/error-messages";
import { ClientHttpError } from "@/lib/http/client-http";
import { reportMutationError, setMutationErrorReporter } from "./mutation-error-reporter";

// Global safety net: any mutation that rejects without its own onError
// triggers this fallback. Prevents silent failures when the network drops
// or the BFF returns non-JSON.
function makeMutationCache(reportError: (message: string) => void) {
  return new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // If the mutation already defines onError (inline or in useMutation options),
      // skip the global handler — the component handles its own errors.
      const hasOwnHandler = mutation.options.onError != null
        || mutation.meta?.skipGlobalError === true;
      if (hasOwnHandler) return;

      const message = error instanceof ClientHttpError ? error.message : GENERIC_ERROR_MESSAGE;

      reportError(message);
    },
  });
}

function makeQueryClient(reportError: (message: string) => void) {
  return new QueryClient({
    mutationCache: makeMutationCache(reportError),
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        gcTime: 5 * 60 * 1000,
        // Only retry on 5xx/network errors — 4xx (401, 403, 404) never succeed on retry.
        retry: (failureCount, error) => {
          if (error instanceof ClientHttpError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 1;
        },
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

/**
 * Reaches the toast from the root layout, which is above every route group.
 *
 * This provider used to mount its own `ToastProvider` to guarantee that reach. The toast now
 * lives in the root layout (`app/layout.tsx`), for reasons that are about behaviour and not
 * weight, so mounting a second one here would give the tree two toast viewports and let a
 * message die on a navigation that leaves this group.
 *
 * The client is created once (rebuilding it would drop the cache), and the toast is wired
 * through `mutation-error-reporter` — the cache publishes, this effect subscribes.
 */
function QueryClientBoundary({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const [queryClient] = useState(() => makeQueryClient(reportMutationError));

  useEffect(() => setMutationErrorReporter(showToast), [showToast]);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  return <QueryClientBoundary>{children}</QueryClientBoundary>;
}
